import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DEX_VALUES } from '@shared/constants/dex-values';
import { DestroySubscription } from '@shared/helpers/destroy-subscription';
import { TenantsInterface } from '@shared/interfaces/tenants.interface';
import { combineLatest, finalize, map, Observable, startWith, take } from 'rxjs';
import { TenantsService } from 'src/app/page/tenants/services/tenants.service';
import {
    MigrationSnipeStatusEnum,
    MigrationSnipeWizardStepEnum,
    MigrationSniperInterface,
} from '../../migration-sniper.interfaces';
import { MigrationSniperService } from '../../migration-sniper.service';
import { NavigateService } from '@core/services/navigate.service';
import { MIGRATION_SNIPER_STATUS_LABELS } from '../../migration-sniper.const';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { FILTER_OPTIONS } from './migration-sniper-list.data';

@Component({
    selector: 'app-migration-sniper-list',
    templateUrl: './migration-sniper-list.component.html',
    styleUrls: ['./migration-sniper-list.component.less'],
    standalone: false,
})
export class MigrationSniperListComponent extends DestroySubscription implements OnInit {
    MigrationSnipeStatusEnum = MigrationSnipeStatusEnum;
    MIGRATION_SNIPER_STATUS_LABELS = MIGRATION_SNIPER_STATUS_LABELS;
    DEX_VALUES = DEX_VALUES;
    tenants$: Observable<TenantsInterface[]>;
    migrationSnipers$: Observable<MigrationSniperInterface[]>;
    loading = false;

    filtersOptions = FILTER_OPTIONS;

    filterForm = new FormGroup({
        dex: new FormControl([]),
        baseToken: new FormControl(''),
        tenant: new FormControl([]),
    });

    constructor(
        private readonly tenantsService: TenantsService,
        private readonly migrationSniperService: MigrationSniperService,
        private readonly navigateService: NavigateService,
        private readonly notificationService: NzNotificationService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.getTenants();

        this.migrationSnipers$ = combineLatest([
            this.migrationSniperService.migrationSnipers$,
            this.filterForm.valueChanges.pipe(startWith(this.filterForm.value)),
        ]).pipe(
            map(([migrationSnipers, filter]) => {
                if (filter.dex.length) {
                    migrationSnipers = migrationSnipers.filter((migrationSniper) =>
                        filter.dex.includes(migrationSniper.dex),
                    );
                }
                if (filter.baseToken) {
                    migrationSnipers = migrationSnipers.filter((migrationSniper) =>
                        migrationSniper.baseToken.address
                            .toLowerCase()
                            .includes(filter.baseToken.toLowerCase()),
                    );
                }
                if (filter.tenant.length) {
                    migrationSnipers = migrationSnipers.filter((migrationSniper) =>
                        filter.tenant.includes(migrationSniper.walletsTenantId),
                    );
                }
                return migrationSnipers;
            }),
        );

        this.migrationSniperService.updateMigrationSnipers().pipe(take(1)).subscribe();
    }

    getTenants() {
        this.tenants$ = this.tenantsService.getAllTenants();
    }

    navigateToProfile(event: Event) {
        event.stopPropagation();
    }

    navigateToMigrationSniper(event: Event, migrationSniper: MigrationSniperInterface) {
        event.preventDefault();
        this.navigateService.navigateToMigrationSniperEdit(
            migrationSniper.id,
            migrationSniper.snipeWizardStep,
        );
    }

    preventDefault(e: Event) {
        e.preventDefault();
        e.stopPropagation();
    }

    archive(profile: MigrationSniperInterface) {
        if (this.loading) return;
        this.loading = true;
        this.migrationSniperService
            .archiveMigrationSniper(profile.id)
            .pipe(
                finalize(() => (this.loading = false)),
                take(1),
            )
            .subscribe(() => {
                const message = 'The migration sniper has been archived successfully';
                this.notificationService.success('Success', message);
            });
    }

    statusFilterFn(list: MigrationSnipeStatusEnum[], item: MigrationSniperInterface) {
        return list.some((status) => item.status === status);
    }

    stepFilterFn(list: MigrationSnipeWizardStepEnum[], item: MigrationSniperInterface) {
        return list.some((step) => item.snipeWizardStep === step);
    }

    trackById(index: number, item: MigrationSniperInterface) {
        return item.id;
    }
}
